import type { ReactNode } from "react";
import { Button, type ButtonColor } from "@legal-500/legal500-design-system";
import type { BulkResultAction } from "@/components/Modal/BulkResultModal";
import styles from "./BulkActionsBar.module.css";

type BulkActionsBarProps = {
  selectedCount: number;
  counts: Record<BulkResultAction, number>;
  blockedReason: string | null;
  running: BulkResultAction | null;
  onRun: (action: BulkResultAction) => void;
  onClearSelection: () => void;
  children?: ReactNode;
};

const ACTIONS: {
  action: BulkResultAction;
  label: string;
  busyLabel: string;
  color: ButtonColor;
}[] = [
  { action: "approve", label: "Approve Selected", busyLabel: "Approving...", color: "success" },
  { action: "reject", label: "Reject Selected", busyLabel: "Rejecting...", color: "error" },
  { action: "suspend", label: "Suspend Selected", busyLabel: "Suspending...", color: "warning" },
];

export function BulkActionsBar({
  selectedCount,
  counts,
  blockedReason,
  running,
  onRun,
  onClearSelection,
  children,
}: BulkActionsBarProps) {
  return (
    <div className={styles.actionsRow}>
      <span className={styles.selectedCount}>{selectedCount} selected</span>
      {blockedReason && (
        <span className={styles.selectionHint} role="status">
          {blockedReason}
        </span>
      )}
      <Button
        variant="text"
        size="small"
        className={styles.clearSelection}
        onClick={onClearSelection}
        disabled={selectedCount === 0}
      >
        Clear selection
      </Button>
      {ACTIONS.map(({ action, label, busyLabel, color }) => (
        <Button
          key={action}
          variant="contained"
          color={color}
          size="small"
          onClick={() => onRun(action)}
          disabled={counts[action] === 0 || running !== null || blockedReason !== null}
          title={blockedReason ?? undefined}
        >
          {running === action
            ? busyLabel
            : counts[action] > 0
              ? `${label} (${counts[action]})`
              : label}
        </Button>
      ))}
      {children}
    </div>
  );
}
